// Pool of medieval-themed temp names paired with deterministic DiceBear seeds.
// Each entry yields a unique avatar via the DiceBear "adventurer" style.

export const AVATAR_POOL = [
  { seed: "aldric_001", name: "Sir Aldric" },
  { seed: "yseult_002", name: "Dame Yseult" },
  { seed: "bran_003", name: "Sir Bran" },
  { seed: "cadoc_004", name: "Brother Cadoc" },
  { seed: "eilwen_005", name: "Sister Eilwen" },
  { seed: "galen_006", name: "Sir Galen" },
  { seed: "hadrian_007", name: "Lord Hadrian" },
  { seed: "ingrith_008", name: "Lady Ingrith" },
  { seed: "joscelin_009", name: "Sir Joscelin" },
  { seed: "kerwin_010", name: "Master Kerwin" },
  { seed: "lanora_011", name: "Dame Lanora" },
  { seed: "madoc_012", name: "Sir Madoc" },
  { seed: "niall_013", name: "Brother Niall" },
  { seed: "orrin_014", name: "Sir Orrin" },
  { seed: "petra_015", name: "Dame Petra" },
  { seed: "quinlan_016", name: "Sir Quinlan" },
  { seed: "roderic_017", name: "Lord Roderic" },
  { seed: "sebold_018", name: "Sir Sebold" },
  { seed: "theron_019", name: "Brother Theron" },
  { seed: "una_020", name: "Dame Una" },
  { seed: "vespian_021", name: "Sir Vespian" },
  { seed: "wystan_022", name: "Master Wystan" },
  { seed: "xanthe_023", name: "Dame Xanthe" },
  { seed: "yvain_024", name: "Sir Yvain" },
  { seed: "zoran_025", name: "Lord Zoran" },
];

export const avatarUrl = (seed) =>
  seed
    ? `https://api.dicebear.com/9.x/adventurer/svg?seed=${encodeURIComponent(seed)}&radius=50&backgroundColor=f8e08a,e8c76a,d9b658`
    : "";

export function pickRandomPair() {
  return pickRandomGroup(2);
}

export function pickRandomGroup(n = 8) {
  const pool = [...AVATAR_POOL];
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, Math.min(n, pool.length));
}

// Hand-drawn "grim & picturesque" tavern portraits stored in /public/tavern-avatars/.
// Each seed is the bare filename (no extension); resolve via tavernAvatarUrl().
// IMPORTANT: each entry's `gender` field MUST match the visual gender of the
// generated portrait so that titles like "Sir" / "Brother" never paint over a
// female image and "Lady" / "Mother" never paint over a male image.
// All female portraits depict YOUNG OR MIDDLE-AGED women (≤55) — no crones.
// Per user iter M642/M643: names simplified — "the X" suffixes removed
// from every entry EXCEPT Cerys (restored to "Cerys the Seer" per
// iter M643 user direction).  Anselma and Mara both promoted to
// "Lady" titles in M643 and their portraits regenerated to match.
// The two Brother (monk) entries were deleted in M642.
export const TAVERN_AVATAR_POOL = [
  { seed: "knight_red",      name: "Sir Roderic",     gender: "m" },
  { seed: "knight_blue",     name: "Dame Yseult",     gender: "f" },
  { seed: "warrior_blue",    name: "Bryn",            gender: "m" },
  { seed: "bard_red",        name: "Cassian",         gender: "m" },
  { seed: "peasant_red",     name: "Wat",             gender: "m" },
  { seed: "peasant_blue",    name: "Lady Mara",       gender: "f" },
  { seed: "noblewoman_red",  name: "Lady Vesper",     gender: "f" },
  { seed: "noblewoman_blue", name: "Lady Ali\u00e9nor", gender: "f" },
  { seed: "huntsman_red",    name: "Cuddy",           gender: "m" },
  { seed: "scholar_red",     name: "Master Aldous",   gender: "m" },
  { seed: "archer_blue",     name: "Fenwick",         gender: "m" },
  { seed: "barmaid_blue",    name: "Lady Anselma",    gender: "f" },
  { seed: "seer_red",        name: "Cerys the Seer",  gender: "f" },
];

const TAVERN_SEED_SET = new Set(TAVERN_AVATAR_POOL.map((e) => e.seed));

export const tavernAvatarUrl = (seed) =>
  seed && TAVERN_SEED_SET.has(seed) ? `/tavern-avatars/${encodeURIComponent(seed)}.jpg` : "";

export function pickRandomTavernPair() {
  return pickRandomTavernGroup(2);
}

export function pickRandomTavernGroup(n = 8) {
  const pool = [...TAVERN_AVATAR_POOL];
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, Math.min(n, pool.length));
}

// Wu-Tang-themed Wu-Pong portraits stored in /public/wupong-avatars/.
// 14 of a planned 15 portraits (the 15th, dj_clockwork, was not generated
// because the Universal Key budget was exceeded mid-run — top up and run
// `python3 /app/backend/scripts/gen_wupong_avatars.py dj_clockwork` to add).
export const WUPONG_AVATAR_POOL = [
  { seed: "mc_thunderclap",     name: "MC Thunderclap",    gender: "m" },
  { seed: "cipher_raven",       name: "Cipher Raven",      gender: "m" },
  { seed: "nightstalker_k",     name: "Nightstalker K",    gender: "m" },
  { seed: "steel_toe",          name: "Steel Toe",         gender: "m" },
  { seed: "kid_callisto",       name: "Kid Callisto",      gender: "m" },
  { seed: "solomon_beats",      name: "Solomon Beats",     gender: "m" },
  { seed: "smoke_ghost",        name: "Smoke Ghost",       gender: "m" },
  { seed: "the_gravedigger",    name: "The Gravedigger",   gender: "m" },
  { seed: "ninth_chamber_sage", name: "9th Chamber Sage",  gender: "m" },
  { seed: "viper_v",             name: "Viper V",           gender: "m" },
  { seed: "the_monk_of_wires",  name: "The Monk of Wires", gender: "m" },
  { seed: "madame_mercury",     name: "Madame Mercury",    gender: "f" },
  { seed: "queen_cassidy",      name: "Queen Cassidy",     gender: "f" },
  { seed: "rhinestone_roz",     name: "Rhinestone Roz",    gender: "f" },
];

const WUPONG_SEED_SET = new Set(WUPONG_AVATAR_POOL.map((e) => e.seed));

export const wupongAvatarUrl = (seed) =>
  seed && WUPONG_SEED_SET.has(seed) ? `/wupong-avatars/${encodeURIComponent(seed)}.jpg` : "";

export function pickRandomWupongGroup(n = 14) {
  const pool = [...WUPONG_AVATAR_POOL];
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, Math.min(n, pool.length));
}

/**
 * Pool-agnostic avatar URL resolver. Dispatches to tavernAvatarUrl,
 * wupongAvatarUrl, or dicebear avatarUrl based on which pool the seed
 * belongs to. PongGame / Controller / ChatRoom use this so that a single
 * `<img src={resolveAvatarUrl(seed)}>` works regardless of which gallery
 * the seed came from.
 */
export const resolveAvatarUrl = (seed) => {
  if (!seed) return "";
  if (WUPONG_SEED_SET.has(seed)) return wupongAvatarUrl(seed);
  if (TAVERN_SEED_SET.has(seed)) return tavernAvatarUrl(seed);
  return avatarUrl(seed);
};
